import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { NumericFormat } from "react-number-format";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "calc(100% - 32px)",
  maxWidth: 380,
  bgcolor: "#ffffff",
  borderRadius: "16px",
  boxShadow: "0 20px 50px rgba(15,23,42,0.18)",
  p: "32px 24px 28px",
  outline: "none",
};

const iconHaloStyle = {
  width: 86,
  height: 86,
  borderRadius: "50%",
  bgcolor: "#f1f4ff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mx: "auto",
  mb: 2,
};

const iconStyle = {
  width: 52,
  height: 52,
  borderRadius: "50%",
  bgcolor: "#ff3100",
  color: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 12px 24px #ff3100",
};

const oldValueBoxStyle = {
  borderRadius: "10px",
  bgcolor: "#f7f9ff",
  border: "1px solid #edf1fb",
  px: 2,
  py: 1.5,
};

function parseCurrencyValue(value) {
  if (value === null || value === undefined || value === "") return NaN;
  if (typeof value === "number") return Number.isFinite(value) ? value : NaN;

  const normalized = String(value)
    .trim()
    .replace(/[^\d,.-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
}

function formatCurrency(value) {
  const parsed = parseCurrencyValue(value);
  if (!Number.isFinite(parsed)) return "";

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(parsed);
}

export default function FreightEditemodSuccessModaTest({
  open,
  handleClose,
  onResult,
  calculateGrossFreightProposedByShipper,
  dataMsg,
  title = "Atualizar valor bruto",
}) {
  const oldValue = useMemo(
    () =>
      parseCurrencyValue(
        dataMsg?.oldValue ?? dataMsg?.value ?? dataMsg?.total ?? 0,
      ),
    [dataMsg],
  );
  const [grossValue, setGrossValue] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setGrossValue(Number.isFinite(oldValue) ? oldValue : "");
    setError("");
  }, [open, oldValue]);

  function handleConfirmClick() {
    const newValue = parseCurrencyValue(grossValue);
    if (!Number.isFinite(newValue) || newValue < 0) {
      setError("Informe um valor bruto válido.");
      return;
    }

    calculateGrossFreightProposedByShipper(newValue);

    onResult?.({
      oldValue: Number.isFinite(oldValue) ? oldValue : 0,
      newValue,
      formattedOldValue: formatCurrency(oldValue),
      formattedNewValue: formatCurrency(newValue),
    });
    handleClose?.();
  }

  return (
    <Modal
      keepMounted
      open={open}
      onClose={handleClose}
      aria-labelledby="gross-value-modal-title"
      aria-describedby="gross-value-modal-description"
    >
      <Box sx={modalStyle}>
        <Box sx={iconHaloStyle}>
          <Box sx={iconStyle}>
            <CheckRoundedIcon />
          </Box>
        </Box>

        <Typography
          id="gross-value-modal-title"
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#111827",
            textAlign: "center",
            mb: 1,
          }}
        >
          {title}
        </Typography>

        <Typography
          id="gross-value-modal-description"
          sx={{
            color: "#4b5563",
            fontSize: 13,
            lineHeight: 1.45,
            textAlign: "center",
            mb: 2.5,
          }}
        >
          Informe o novo valor bruto. O valor antigo fica visível para
          conferência antes de confirmar.
        </Typography>

        <Stack spacing={2}>
          <Box sx={oldValueBoxStyle}>
            <Typography sx={{ color: "#6b7280", fontSize: 12, mb: 0.5 }}>
              Valor bruto antigo
            </Typography>
            <Typography sx={{ color: "#111827", fontWeight: 700 }}>
              {formatCurrency(oldValue)}
            </Typography>
          </Box>

          <NumericFormat
            customInput={TextField}
            fullWidth
            label="Novo valor bruto"
            value={grossValue}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            fixedDecimalScale
            prefix="R$ "
            allowNegative={false}
            onValueChange={(values) => {
              setGrossValue(values.floatValue ?? "");
              setError("");
            }}
            error={Boolean(error)}
            helperText={error || " "}
            inputProps={{ inputMode: "decimal" }}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={handleConfirmClick}
            sx={{
              bgcolor: "#f6f8ff",
              color: "#111827",
              borderRadius: "8px",
              py: 1.25,
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#edf2ff",
                boxShadow: "none",
              },
            }}
          >
            Confirmar novo valor
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
}
